// src/components/VideoComponent.js
import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useEffect, useMemo, useState} from 'react';
import { Link, useMatch, useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useGateAccess } from '../hooks/useGateAccess';
import DigitalAssetPurchase from './DigitalAssetPurchase';
import ComponentControls from './ComponentControls';
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from './TimestampBadge';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { auth } from '../firebaseConfig';
import { buildPanelClassName } from '../utils/panelTheme';
import { queueSocialFeedShareDraft } from '../utils/socialFeedShareDraft';

export default function VideoComponent({
                                           comp,
                                           index,
                                           moveUp,
                                           moveDown,
                                           onEdit,
                                           isEditor = false,
                                           removeComponent,
                                           ownerUsername,
                                           // gating props
                                           gateType,
                                           selectedListId,
                                           onGateTypeChange,
                                           onGateListChange,
                                           extraGates,
                                           gateLogic,
                                           onExtraGatesChange,
                                           onGateLogicChange,

                                           donationCampaignOptions = [],
                                           donationGate,
                                           onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,

                                           style = {},
                                           onStyleChange,
                                           coverUrl = '',
                                           onCoverChange,
                                           onPanelBackgroundImageChange,

                                           timestampDisplay = 'none',
                                           onTimestampDisplayChange,

                                           // optional display mode (detail pages pass "full")
                                           displayMode,
                                           ownerPublicKey = ''
                                       }) {
    // ----- Gate composition (mirrors TextComponent) -----
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const primaryGate = normalizeGate(comp.gate || { type: 'public', listId: '' });
    const gates = useMemo(
        () => [
            primaryGate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [primaryGate, comp.extraGates, ownerPublicKeyStr]
    );
    const [gateRefreshKey, setGateRefreshKey] = useState(0);

    const logic = comp.gateLogic === 'all' ? 'all' : (gateLogic || 'any');

    const donationMap = useMemo(() => {
        const m = new Map();
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] = useGateAccess({
        gates,
        logic,
        donationLookup: donationMap
    }, { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id });
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const navigate = useNavigate();
    const [viewerUid, setViewerUid] = useState(() => String(auth?.currentUser?.uid || ""));
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setViewerUid(String(u?.uid || ""));
        });
        return () => unsub?.();
    }, []);

    // ----- Deep link for detail page (robust username resolution) -----
    const { username: routeUsername } = useParams();
    const isDetailPage = useMatch('/:username/v/:componentId');
    const fallbackUser =
        ownerUsername ||            // ← prefer prop from MuultiPage
        comp?.ownerUsername ||
        comp?.muultiName ||
        '';
    const safeUsername = routeUsername || fallbackUser;
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);
    const detailHref =
        safeUsername && comp?.id
            ? `/${safeUsername}/v/${comp.id}`
            : null;
    const shareGateInput = useMemo(() => {
        const mapped = (gates || [])
            .map((g) => {
                const type = String(g?.type || 'public');
                if (type === 'public') return { type: 'public' };
                if (type === 'donation') {
                    return {
                        type: 'donation',
                        donationCompId: String(g?.donationCompId || ''),
                        donationThreshold: Number(g?.donationThreshold || 0),
                        campaignName: String(g?.campaignName || ''),
                        currency: String(g?.currency || ''),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'directPayment') {
                    return {
                        type: 'directPayment',
                        priceSol: Number(g?.priceSol || 0),
                        payToUid: String(g?.payToUid || ownerPublicKeyStr || ''),
                        targetId: String(g?.targetId || comp?.id || ''),
                        currency: String(g?.currency || 'SOL'),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'tokensHeld') {
                    return {
                        type: 'tokensHeld',
                        tokenThreshold: Number(g?.tokenThreshold || 0),
                        currency: String(g?.currency || 'SOL'),
                        tokenMint: String(g?.tokenMint || ''),
                    };
                }
                if (type === 'token' || type === 'collection' || type === 'compressedCollection') {
                    return {
                        type,
                        listId: String(g?.listId || ''),
                    };
                }
                return null;
            })
            .filter(Boolean);
        return {
            logic: logic === 'all' ? 'all' : 'any',
            gates: mapped.length ? mapped : [{ type: 'public' }],
        };
    }, [gates, logic, ownerPublicKeyStr, comp?.id]);
    const canShareToSocialFeed = Boolean(detailHref && viewerUid);
    const showInlineShareButton = canShareToSocialFeed;
    const handleShareToSocialFeed = () => {
        if (!canShareToSocialFeed || !detailHref) return;

        const shareUrl = typeof window !== 'undefined'
            ? `${window.location.origin}${detailHref}`
            : detailHref;
        const title = String(comp?.title || '').trim();
        const summary = String(comp?.description || '').trim();
        const contentParts = [title, summary, shareUrl].filter(Boolean);

        queueSocialFeedShareDraft({
            source: 'video',
            detailPath: detailHref,
            content: contentParts.join('\n\n'),
            summary,
            tags: ['video'],
            targetId: String(comp?.id || ''),
            gateInput: shareGateInput,
        });
        navigate('/posts');
    };

    // ----- Thumbnail: tolerate both camelings -----
    const thumb =
        comp?.featureImageUrl ||
        null;

    const mode = (displayMode || comp?.displayMode || 'compressed');
    const canLinkToDetail = !!detailHref && !isDetailPage;
    const detailLinkStyle = {
        color: style?.color || 'inherit',
        textDecoration: 'underline',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer'
    };

    // Respect display mode:
    // - Detail page passes displayMode = 'full' → always show the video player
    // - Otherwise: if there’s a feature image, show it; if not, show the video player
    const showPlayer = mode === 'full' || !thumb;
    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    const renderThumbnail = () => {
        if (!thumb) return null;

        const body = (
            <div className="video-thumbnail-wrapper" aria-label="Open video detail">
                <img
                    src={thumb}
                    alt={comp.title || "Video feature image"}
                    className="video-thumbnail-image"
                />
                <div className="video-play-button-overlay" aria-hidden="true" />
            </div>
        );
        if (canLinkToDetail) {
            return (
                <Link to={detailHref} className="detail-page-return-link" style={{ display: 'block' }}>
                    {body}
                </Link>
            );
        }
        return body;
    };

    // ----- Loading / Locked states -----
    if (isLoading) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                {comp.title && <h1>{comp.title}</h1>}
                {comp.description && <p>{comp.description}</p>}
                <h3>Checking access for {gateNamesLabel}…</h3>
                <TimestampBadge
                    mode={comp.timestampDisplay || timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={primaryGate.type}
                    selectedListId={primaryGate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                {comp.title && <h1>{comp.title}</h1>}
                {comp.description && <p>{comp.description}</p>}
                <h1>{deniedHeading} - {gateNamesLabel}</h1>
                {passwordGate ? (
                    <PasswordUnlock
                        gate={passwordGate}
                        componentId={comp.id}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {directPaymentGate ? (
                    <DirectPaymentUnlock
                        gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                <TimestampBadge
                    mode={comp.timestampDisplay || timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {showInlineShareButton && (
                    <div style={{ marginTop: 8 }}>
                        <button
                            type="button"
                            className="muulti-button"
                            onClick={handleShareToSocialFeed}
                        >
                            Share to Social Feed
                        </button>
                    </div>
                )}
                {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={primaryGate.type}
                    selectedListId={primaryGate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    // ----- Main render (now respecting showPlayer) -----
    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            {comp.title && <h1>{comp.title}</h1>}
            {comp.description && <p>{comp.description}</p>}

            {showPlayer ? (
                comp?.url ? (
                    <video
                        src={comp.url}
                        controls
                        controlsList="nodownload"
                        style={{ width: "100%", borderRadius: 8 }}
                    />
                ) : null
            ) : (
                renderThumbnail()
            )}
            {canLinkToDetail ? (
                <div style={{ marginTop: 8 }}>
                    <Link to={detailHref} style={detailLinkStyle}>
                        View More ->
                    </Link>
                </div>
            ) : null}
            {false ? (
                <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {canLinkToDetail ? (
                        <Link to={detailHref} target="_blank" rel="noreferrer" style={detailLinkStyle}>
                            Open in new page †’
                        </Link>
                    ) : null}
                    {showInlineShareButton ? (
                        <button
                            type="button"
                            className="muulti-button"
                            onClick={handleShareToSocialFeed}
                        >
                            Share to Social Feed
                        </button>
                    ) : null}
                </div>
            ) : null}
            {hasAccess && (
                <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {showInlineShareButton && (
                <div style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        className="muulti-button"
                        onClick={handleShareToSocialFeed}
                    >
                        Share to Social Feed
                    </button>
                </div>
            )}
            {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}

            <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={primaryGate.type}
                selectedListId={primaryGate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
            />
        </div>
        </GateRevealPanel>
    );
}




